import React, { use, useEffect, useRef, useState } from 'react'
import './collaborators-landing.styles.scss';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import { useLocation, useNavigate } from 'react-router-dom';
import CollaboratorsOrg from './collaborators-org.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import CollaboratorsIndividual from './collaborators-ind.component';
import { useSelector } from 'react-redux';
import { UseCloudFunction } from '@/utils/firebase.utils';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { ModalConfirm } from '@/components/modal/modal.component';
import { getCollectionFromDb, getUniqueDocumentFromDb } from '@/utils/getDataFromDb';
import { getInvitesFromDb } from '@/utils/getAdminData';
import useAnalytics from '@/utils/useAnalytics';

const CollaboratorsLanding = ({
  gameData,
  orgs,
}) => {

  const userProfile = useSelector((state) => state.userData.userProfile);
  const membership = useSelector(state => state.userData.membership);
  const [ hasAccess, setHasAccess ] = useState(false);
  const [ collabSystem, setCollabSystem ] = useState(null);
  const [ regsData, setRegsData ] = useState(null);
  const [ confirmMessage, setConfirmMessage ] = useState(null);
  const [ pendingInvites, setPendingInvites ] = useState(null);
  const { trackEvent, trackException } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();
  const approveRef = useRef(null);
  const invitesRef = useRef({});
  let regsListener;
  let invitesListener

  useEffect(() => {
    return () => {
      if (regsListener) regsListener();
      if (invitesListener) invitesListener();
    }
  }, [])

  useEffect(() => {
    if (!gameData || !userProfile) return;
    checkForAccess();
  }, [gameData, userProfile])

  function goBack() {
    if (collabSystem) {
      setCollabSystem(null);
    } else {
      navigate(-1);
    }
  }

  async function checkForAccess() {
    const isOwner = await UseCloudFunction('checkManagerAccess', {
      'appId': userProfile.appId,
      'gameId': gameData.gameId
    });
    if (isOwner.success && isOwner.roles.includes('owner')) {
      setHasAccess(true);
      getRegistrations();
      getInvites();
      // if (gameData.orgId) {
      //   setCollabSystem('org');
      // }
    } else {
      navigate(`/manage/${gameData.path}`);
    }
  }

  function getRegistrations() {
    getUniqueDocumentFromDb({
      'collection': `games/${gameData.gameId}/registrations/`,
      'docId': `${gameData.gameId}-regs`,
      'callback': callback,
      'handleListener': handleListener
    })
    function callback(regDoc) {
      // get count of users with "manager" or "distributer" roles
      const managerCount = Object.values(regDoc.roles).filter(roleArr => 
        roleArr.includes('manager') || roleArr.includes('distributer')
      ).length;
      if (managerCount > 0 && !gameData.orgId) {
        setCollabSystem('premium');
      } else if (gameData.orgId) {
        setCollabSystem('org');
      }
      setRegsData(regDoc);
    }
    function handleListener(unsub) {
      regsListener = unsub;
    }
  }

  function getInvites() {
    invitesRef.current = {};
    setPendingInvites({});
    getCollectionFromDb({
      'collection': `games/${gameData.gameId}/gameInvites`,
      'query1': {
        'key': 'status',
        'condition': '==',
        'value': 'pending'
      },
      'callback': callback,
      'handleListener': handleListener
    })
    function callback(data) {
      invitesRef.current = {
        ...invitesRef.current,
        ...{[data.id]: data}
      };
      console.log(invitesRef.current);
      setPendingInvites(invitesRef.current);
    }
    function handleListener(unsub) {
      invitesListener = unsub;
    }
  }

  function removeCollaborator(appId, inviteId) {
    console.log('removing collab: ' + appId)
    setConfirmMessage(`Are you sure you want to remove this collaborator? They will ` +
      `lose access to this game immediately. They will have to be reinvited to regain access.`);
    approveRef.current = async () => {
      setConfirmMessage(null);
      continueRemoveCollaborator(appId, inviteId);
    }

    async function continueRemoveCollaborator(appId, inviteId) {
      const res = await UseCloudFunction(
        'removeGameCollaborator',
        {
          'gameId': gameData.gameId,
          'appId': userProfile.appId,
          'collabAppId': appId,
          'inviteId': inviteId,
        }
      )
      console.log(res);
      if (res.error) {
        console.log('error removing collaborator: ' + res.errorMessage);
        trackException('Error removing collaborator: ' + res.errorMessage);
        return;
      }
      getInvites();
      trackEvent(
        'collaborator_removed', 
        'Game Manager',
        `Removed collaborator from game`,
        null,
        membership
      )
    }
  }

  return (
    <div className='collaborators-landing'>
      {
        (hasAccess && regsData) 
        ?
        <div className='g-card collab-card'>
          <BackButton cancel={() => goBack()} />
          <div className="g-space-1"></div>
          <div className='card-title'>
            Settings: Collaborators
          </div>
          <div className="g-space-1"></div>
          <div className='card-subtitle'>
            Manage who has access to this game and what they can do.
          </div>
          <div className="g-space-1"></div>
          <div className='collab-content'>
            {
              (gameData && !gameData.orgId && !collabSystem) 
              ?
              <div className='button-group'>
                <button 
                  className={`g-button ${gameData.orgId ? 'disabled' : ''}`} 
                  onClick={() => setCollabSystem('premium')}>
                  <ImageContainer src="/images/icons/premium.png" className='icon' />
                  Invite a Premium Member
                </button>
                {
                  (orgs) &&
                  <button className='g-button' onClick={() => setCollabSystem('org')}>
                    <ImageContainer src="/images/icons/enterprise.png" className='icon' />
                    Invite a Member of Your Organization
                  </button>
                }
              </div>
              :
              (gameData && collabSystem === 'org')
              ?
              <CollaboratorsOrg
                gameData={gameData}
                orgs={orgs}
                pendingInvites={pendingInvites}
                setCollabSystem={setCollabSystem}
                removeCollaborator={removeCollaborator} />  
              :
              (gameData && collabSystem === 'premium')
              ?
              <CollaboratorsIndividual 
                gameData={gameData}
                regsData={regsData}
                orgs={orgs}
                pendingInvites={pendingInvites}
                setCollabSystem={setCollabSystem}
                removeCollaborator={removeCollaborator} />
              : null
            }
          </div>
        </div>
        :
        <div className='center'>
          <ThreeDotsLoader />
        </div>
      }
      <ModalConfirm
        show={!!confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
    </div>
  )
}

export default CollaboratorsLanding